/* W8.6 — Ré-estimation versionnée : append-only, snapshots historiques intacts. */
import { useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Plus, Trash2, History } from "lucide-react";

const METHODS = ["expert", "historical", "model", "market", "autre"];

export function ReestimateForm({ decision, onDone }) {
  const locked = decision.status === "decided"; // scénarios existants seulement
  const [reason, setReason] = useState("");
  const [cost, setCost] = useState(decision.cost_chf ?? 0);
  const [scenarios, setScenarios] = useState((decision.scenarios || []).map((s) => ({
    id: s.id, label: s.label, payoff_chf: s.payoff_chf,
    probability: { value: s.probability.value, source: s.probability.source, method: s.probability.method, confidence: s.probability.confidence },
  })));
  const [busy, setBusy] = useState(false);
  const upd = (i, patch, probPatch) => setScenarios((arr) => arr.map((x, j) => j === i ? { ...x, ...patch, probability: { ...x.probability, ...(probPatch || {}) } } : x));
  const submit = async () => {
    if (!reason.trim()) return notify.error("Raison de la ré-estimation requise (traçabilité T0→Tn).");
    if (scenarios.some((s) => !s.probability.source.trim())) return notify.error("Chaque probabilité ré-estimée exige une source.");
    setBusy(true);
    try {
      await api.post(`/workspace/decisions/${decision.id}/reestimate`, {
        reason: reason.trim(), cost_chf: Number(cost) || 0,
        scenarios: scenarios.map((s) => ({
          id: s.id || null, label: s.label, payoff_chf: Number(s.payoff_chf) || 0,
          probability: { ...s.probability, value: Number(s.probability.value), confidence: Number(s.probability.confidence) },
        })),
      });
      notify.success("Conviction ré-estimée — nouvelle version journalisée, l'historique reste intact.");
      onDone();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className="p-2.5 rounded border border-[#7F77DD]/30 bg-[#7F77DD]/5 space-y-2" data-testid={`reestimate-form-${decision.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#7F77DD] flex items-center gap-1.5">
        <History className="w-3 h-3" /> Ré-estimation v{(decision.estimation_version || 1) + 1} — append-only, versions précédentes conservées
      </div>
      {locked && <div className="text-[10px] text-zinc-500">Décision figée : ré-estimation des scénarios existants uniquement (probabilités, payoffs) — le snapshot de décision reste immuable.</div>}
      {scenarios.map((s, i) => (
        <div key={s.id || i} className="grid grid-cols-2 lg:grid-cols-6 gap-1.5 p-2 rounded bg-white/[0.03]">
          <input value={s.label} disabled={locked} onChange={(e) => upd(i, { label: e.target.value })} className="noam-input text-[10px] lg:col-span-2 disabled:opacity-60" placeholder="Scénario…" />
          <input type="number" step="0.05" min="0" max="1" value={s.probability.value} onChange={(e) => upd(i, {}, { value: e.target.value })} className="noam-input text-[10px]" title="Probabilité (0-1)" data-testid={`reestimate-prob-${i}`} />
          <input type="number" value={s.payoff_chf} onChange={(e) => upd(i, { payoff_chf: e.target.value })} className="noam-input text-[10px]" title="Payoff CHF" data-testid={`reestimate-payoff-${i}`} />
          <input value={s.probability.source} onChange={(e) => upd(i, {}, { source: e.target.value })} placeholder="Source *" className="noam-input text-[10px]" data-testid={`reestimate-source-${i}`} />
          <div className="flex gap-1">
            <select value={s.probability.method} onChange={(e) => upd(i, {}, { method: e.target.value })} className="noam-input text-[10px] flex-1">
              {METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
            <input type="number" step="0.1" min="0" max="1" value={s.probability.confidence} onChange={(e) => upd(i, {}, { confidence: e.target.value })} className="noam-input text-[10px] w-14" title="Confiance (0-1)" data-testid={`reestimate-confidence-${i}`} />
            {!locked && <button onClick={() => setScenarios((x) => x.filter((_, j) => j !== i))} className="btn-ghost p-1 text-[#E24B4A]"><Trash2 className="w-3 h-3" /></button>}
          </div>
        </div>
      ))}
      <div className="flex gap-1.5 items-center flex-wrap">
        {!locked && (
          <button onClick={() => setScenarios((s) => [...s, { id: null, label: "", payoff_chf: 0, probability: { value: 0.5, source: "", method: "expert", confidence: 0.5 } }])} className="btn-ghost text-[10px]">
            <Plus className="w-3 h-3" /> Scénario
          </button>
        )}
        <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="noam-input text-[10px] w-28" title="Coûts TX (CHF)" placeholder="Coûts TX" />
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Raison de la ré-estimation * (nouvelle info, marché…)" className="noam-input text-[10px] flex-1 min-w-[180px]" data-testid={`reestimate-reason-${decision.id}`} />
        <button onClick={submit} disabled={busy} className="btn-primary text-[10px]" data-testid={`reestimate-submit-${decision.id}`}>{busy ? "…" : "Journaliser v" + ((decision.estimation_version || 1) + 1)}</button>
      </div>
    </div>
  );
}

export function EstimationHistory({ decision }) {
  const versions = decision.estimations || [];
  if (!versions.length) return null;
  const fmt = (v) => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v);
  return (
    <div className="space-y-0.5" data-testid={`estimation-history-${decision.id}`}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-600">Convictions versionnées (immuables)</div>
      {versions.map((e) => (
        <div key={e.version} className="text-[10px] text-zinc-500 flex gap-2 items-center flex-wrap">
          <span className="font-mono text-[#7F77DD]">v{e.version}</span>
          <span>{e.at ? new Date(e.at).toLocaleString("fr-CH") : "—"}</span>
          <span>EV {fmt(e.economics?.ev_chf)}</span>
          <span>AdjEV {fmt(e.economics?.adjusted_ev_chf)}</span>
          <span>Conf. {e.economics?.confidence_avg != null ? Math.round(e.economics.confidence_avg * 100) + "%" : "—"}</span>
          <span className="text-zinc-600 truncate max-w-[220px]" title={e.reason}>{e.reason}</span>
          {e.by_email && <span className="text-zinc-600">· {e.by_email}</span>}
        </div>
      ))}
    </div>
  );
}
